// src/components/common/DashboardControls.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ReactSelect from "@/components/common/ReactSelect";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { X, FileDown, Loader2 } from "lucide-react";
import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";
import { usePermissions } from "@/context/PermissionContext";

const DISPLAY_OPTIONS = [
  { label: "Local Currency",     value: "Local Currency"     },
  { label: "Reporting Currency", value: "Reporting Currency" },
];

const CURRENCY_OPTIONS = [
  { label: "Reporting Currency", value: "All Currency" },
  { label: "AED",                value: "AED"          },
  { label: "JOD",                value: "JOD"          },
  { label: "SAR",                value: "SAR"          },
  { label: "TRY",                value: "TRY"          },
];

const buildFileName = (label, year, month) =>
  [label ?? "report", year ?? "", month ?? ""]
    .filter(Boolean)
    .join("_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "") + ".pdf";

const DashboardControls = ({
  selectedContract,
  selectedDisplay,
  selectedCurrency,
  selectedYear,
  selectedMonth,
  onContractChange,
  onContractLabelChange,
  onDisplayChange,
  onCurrencyChange,
  onYearChange,
  onMonthChange,
  activeTabLabel,
  activeTabPermKey,
  printTargetRef,
  onExportingChange,
  tabs,
  activeTab,
  onTabChange,
}) => {
  const [allContracts, setAllContracts] = useState([]); // raw from API — never changes
  const [contracts,    setContracts]    = useState([]); // filtered — shown in dropdown
  const [years,        setYears]        = useState([]);
  const [months,       setMonths]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [pdfLoading,   setPdfLoading]   = useState(false);
  const [tooltip,      setTooltip]      = useState({ visible: false, label: '', x: 0, y: 0 });

  const hasFetched = useRef(false);

  const { allMYContracts } = usePermissions();

  // ── Step 1: Fetch all data once ──────────────────────────────────────────
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiCall("get", "/boardpack/contracts");

        if (response?.data) {
          const contractOptions = response.data.contracts.map((item) => ({
            label: item.description,
            value: item.name,
          }));
          setAllContracts(contractOptions);

          const yearOptions = response.data.years.map((item) => ({
            label: item.description,
            value: item.name,
          }));
          setYears(yearOptions);

          if (!selectedYear && yearOptions.length > 0) {
            const currentYear = new Date().getFullYear().toString();
            const match = yearOptions.find((y) => y.value === currentYear);
            onYearChange(match ? match.value : yearOptions[0].value);
          }

          const monthOptions = response.data.months.map((item) => ({
            label: item.description,
            value: item.name,
          }));
          setMonths(monthOptions);

          if (!selectedMonth && monthOptions.length > 0) {
            const currentMonth = new Date().toLocaleString("en-US", { month: "short" });
            const match = monthOptions.find((m) => m.value === currentMonth);
            onMonthChange(match ? match.value : monthOptions[0].value);
          }

          if (!selectedDisplay)  onDisplayChange("Local Currency");
          if (!selectedCurrency) onCurrencyChange("All Currency");
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 2: Filter contracts based on activeTabPermKey + allMYContracts ───
  // Runs whenever: allContracts load ho, allMYContracts aaye, ya tab switch ho
  useEffect(() => {
    if (allContracts.length === 0) return;

    let filtered;

    if (allMYContracts && activeTabPermKey) {
      // Sirf active tab ke assigned contracts
      const activeList = allMYContracts[activeTabPermKey] ?? [];

      if (activeList.length > 0) {
        filtered = allContracts.filter(c => activeList.includes(c.label));

      } else {
        // Is tab ke liye kuch assign nahi — empty list dikhao
        filtered = [];
      }

      // Agar filter ke baad kuch nahi mila (key mismatch?) toh sab dikhao
      if (filtered.length === 0 && activeList.length === 0) {
        filtered = allContracts;
      }
    } else {
      // allMYContracts null (Admin/not loaded) — sab dikhao
      filtered = allContracts;
    }

    setContracts(filtered);

    // Default contract set karo filtered list se
    if (!selectedContract && filtered.length > 0) {
      const allCompanies = filtered.find(c =>
        c.label.toLowerCase().includes("all")
      );
      const defaultContract = allCompanies ?? filtered[0];
      onContractChange(defaultContract.value);
      onContractLabelChange?.(defaultContract.label);
    }

    // Agar selected contract naye filtered list mein nahi hai toh reset
    if (selectedContract && !filtered.find(c => c.value === selectedContract)) {
      const defaultContract = filtered[0] ?? null;
      onContractChange(defaultContract?.value ?? null);
      onContractLabelChange?.(defaultContract?.label ?? null);
    }

  }, [allContracts, allMYContracts, activeTabPermKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PDF Download ──────────────────────────────────────────────────────────
  const handleDownloadPdf = async () => {
    const target = printTargetRef?.current;
    if (!target) return;

    setPdfLoading(true);
    onExportingChange?.(true);

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await document.fonts.ready;

    const images = target.querySelectorAll("img");
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload  = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 2000);
        });
      })
    );

    await new Promise((r) => setTimeout(r, 1200));

    try {
      const pixelRatio = 2;

      let dataUrl;
      try {
        dataUrl = await htmlToImage.toPng(target, {
          quality:             1.0,
          pixelRatio,
          cacheBust:           true,
          backgroundColor:     "#ffffff",
          preferredFontFormat: "woff",
        });
        if (!dataUrl || !dataUrl.startsWith("data:image/png")) throw new Error("Invalid PNG");
      } catch {
        dataUrl = await htmlToImage.toJpeg(target, {
          quality:         0.95,
          pixelRatio,
          cacheBust:       true,
          backgroundColor: "#ffffff",
        });
      }

      const imgEl = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = dataUrl;
      });

      const pageW = Math.round(imgEl.naturalWidth  / pixelRatio);
      const pageH = Math.round(imgEl.naturalHeight / pixelRatio);

      const pdf = new jsPDF({
        orientation: pageW > pageH ? "l" : "p",
        unit:        "pt",
        format:      [pageW, pageH],
        compress:    false,
      });

      const format = dataUrl.startsWith("data:image/jpeg") ? "JPEG" : "PNG";
      pdf.addImage(dataUrl, format, 0, 0, pageW, pageH, undefined, "FAST");
      pdf.save(buildFileName(activeTabLabel, selectedYear, selectedMonth));

    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      onExportingChange?.(false);
      setPdfLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} />;

  return (
    <div className="w-full">
      <div className="bg-muted border-2 border-border rounded-lg p-2">

        {/* ── Mobile: stacked layout / Desktop: single row ── */}
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-2">

          {/* ── Filters ── */}
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">

            {/* Contract — full width on mobile, fixed on desktop */}
            <div className="w-full sm:w-[190px] lg:w-[200px] relative">
              <ReactSelect
                options={contracts}
                value={contracts.find((c) => c.value === selectedContract) || null}
                onChange={(option) => {
                  onContractChange(option?.value ?? null);
                  onContractLabelChange?.(option?.label ?? null);
                }}
                placeholder="Select Contract"
                width="100%"
                className="text-[12px]"
                maxMenuHeight={400}
                controlHeight="32px"
              />
              {selectedContract && (
                <button
                  onClick={() => { onContractChange(null); onContractLabelChange?.(null); }}
                  className="absolute right-8 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-destructive transition-colors"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Year — half width on mobile */}
            <div className="w-[calc(50%-4px)] sm:w-[85px] lg:w-[90px] relative">
              <ReactSelect
                options={years}
                value={years.find((y) => y.value === selectedYear) || null}
                onChange={(option) => onYearChange(option?.value ?? null)}
                placeholder="Year"
                width="100%"
                className="text-[12px]"
                maxMenuHeight={200}
                controlHeight="32px"
              />
              {selectedYear && (
                <button
                  onClick={() => onYearChange(null)}
                  className="absolute right-8 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-destructive transition-colors"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Month — half width on mobile */}
            <div className="w-[calc(50%-4px)] sm:w-[85px] lg:w-[90px] relative">
              <ReactSelect
                options={months}
                value={months.find((m) => m.value === selectedMonth) || null}
                onChange={(option) => onMonthChange(option?.value ?? null)}
                placeholder="Month"
                width="100%"
                className="text-[12px]"
                maxMenuHeight={300}
                controlHeight="32px"
              />
              {selectedMonth && (
                <button
                  onClick={() => onMonthChange(null)}
                  className="absolute right-8 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-destructive transition-colors"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Display — half width on mobile */}
            <div className="w-[calc(50%-4px)] sm:w-[160px] lg:w-[175px] relative">
              <ReactSelect
                options={DISPLAY_OPTIONS}
                value={DISPLAY_OPTIONS.find((d) => d.value === selectedDisplay) || null}
                onChange={(option) => onDisplayChange(option?.value ?? null)}
                placeholder="Display"
                width="100%"
                className="text-[12px]"
                controlHeight="32px"
              />
              {selectedDisplay && (
                <button
                  onClick={() => onDisplayChange(null)}
                  className="absolute right-8 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-destructive transition-colors"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Currency — half width on mobile */}
            <div className="w-[calc(50%-4px)] sm:w-[160px] lg:w-[175px] relative">
              <ReactSelect
                options={CURRENCY_OPTIONS}
                value={CURRENCY_OPTIONS.find((c) => c.value === selectedCurrency) || null}
                onChange={(option) => onCurrencyChange(option?.value ?? null)}
                placeholder="Currency"
                width="100%"
                className="text-[12px]"
                controlHeight="32px"
              />
              {selectedCurrency && (
                <button
                  onClick={() => onCurrencyChange(null)}
                  className="absolute right-8 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-destructive transition-colors"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

          </div>

          {/* ── Tab icons + PDF — full width on mobile (space-between), auto on desktop ── */}
          <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">

            {/* Tab icon buttons */}
            {tabs?.length > 0 && (
              <>
                <div className="flex items-center p-0.5 bg-muted border border-border rounded-lg gap-0.5 shadow-inner">
                  {tabs.map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => onTabChange?.(tab.value)}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({ visible: true, label: tab.label, x: rect.left + rect.width / 2, y: rect.bottom });
                      }}
                      onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                      className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200
                        ${activeTab === tab.value
                          ? 'bg-background text-primary shadow-sm border border-border font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
                        }`}
                    >
                      {tab.icon}
                    </button>
                  ))}
                </div>
                <div className="w-px h-6 bg-border" />
              </>
            )}

            <button
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              title={`Download ${activeTabLabel ?? "page"} as PDF`}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium
                         bg-primary text-primary-foreground
                         hover:bg-primary/90 active:bg-primary/80
                         disabled:opacity-60 disabled:cursor-not-allowed
                         transition-colors shrink-0"
            >
              {pdfLoading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <FileDown className="w-4 h-4" />
              }
              <span className="hidden sm:inline">
                {pdfLoading ? "Generating…" : "Download PDF"}
              </span>
            </button>

          </div>

        </div>
      </div>

      {tooltip.visible && createPortal(
        <div
          className="pointer-events-none fixed px-2 py-1 text-xs bg-popover text-popover-foreground border border-border rounded shadow-md whitespace-nowrap"
          style={{ left: tooltip.x, top: tooltip.y + 6, transform: 'translate(-50%, 0)', zIndex: 100000 }}
        >
          {tooltip.label}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DashboardControls;